import { useCallback, useEffect, useRef, useState } from 'react';
import { Card } from '../../../shared/ui/Primitives';
import { configIdentity } from '../adapters/configuration-http';
import {
  CONFIG_INSTANCE,
  METRIC_SPEC,
  type ConfigReceipt,
  type ConfigSource,
  type ConfigurationPort,
  type Metric,
} from '../consumer/configuration';
import {
  submitConfigurationMutation,
  useConfigurationSnapshot,
  type MutationResult,
} from '../consumer/configuration-state';
import { useMonitoringRead } from '../consumer/polling';
import { MonitoringNetworkView } from './Network';
import { MonitoringReadNotice } from './Summary';
function ConfigurationHistory({ port }: { readonly port: ConfigurationPort }) {
  const [offset, setOffset] = useState(0);
  const read = useCallback(
    (signal: AbortSignal) => port.history(offset, 20, signal),
    [offset, port],
  );
  const result = useMonitoringRead(`history/${offset}`, read);
  const data = result.state.data;
  return (
    <section data-nm-config-history>
      <h3>配置变更历史 · 后端审计</h3>
      <MonitoringReadNotice state={result.state} retry={result.retry} label="配置历史" />
      {data && (
        <>
          <p>
            后端生成 {data.generatedAt} · 总记录 {data.total} · offset {data.offset}
          </p>
          <div className="nm-table-wrap">
            <table className="nm-table">
              <thead>
                <tr>
                  <th>动作 / 对象</th>
                  <th>对象 / 全局修订</th>
                  <th>原因 / 操作者</th>
                  <th>请求 / 时间</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.requestId}>
                    <td>
                      {item.action}
                      <small>{item.objectId}</small>
                    </td>
                    <td>
                      {item.beforeRevision} → {item.afterRevision}
                      <small>
                        {item.beforeConfigRevision} → {item.afterConfigRevision}
                      </small>
                    </td>
                    <td>
                      {item.reason || '无原因记录'}
                      <small>{item.actor}</small>
                    </td>
                    <td>
                      {item.requestId}
                      <small>{item.recordedAt}</small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.total === 0 && (
            <p className="nm-empty">尚无本轮配置变更记录；历史迁移未伪造旧审计。</p>
          )}
          <div className="nm-pagination">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - 20))}
            >
              上一页配置历史
            </button>
            <button
              type="button"
              disabled={offset + 20 >= data.total || offset >= 100000}
              onClick={() => setOffset(offset + 20)}
            >
              下一页配置历史
            </button>
          </div>
        </>
      )}
    </section>
  );
}
export function ConfigurationPanel({ port }: { readonly port: ConfigurationPort }) {
  const snapshot = useConfigurationSnapshot(port);
  const [open, setOpen] = useState(false);
  const [revision, setRevision] = useState<number | null>(null);
  const [areaDraft, setAreaDraft] = useState({ areaId: '', displayName: '' });
  const [sourceDraft, setSourceDraft] = useState({
    areaId: '',
    sourceId: '',
    displayName: '',
    metric: 'TEMPERATURE' as Metric,
  });
  const [areaRename, setAreaRename] = useState({ id: '', name: '' });
  const [sourceRename, setSourceRename] = useState({ id: '', name: '' });
  const [enableTarget, setEnableTarget] = useState<ConfigSource | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<MutationResult | null>(null);
  const submitting = useRef(false);
  const alive = useRef(true);
  const controller = useRef<AbortController | null>(null);
  // Preserve request identity for an explicit retry after an uncertain transport result.
  const attempt = useRef<{ signature: string; requestId: string } | null>(null);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      controller.current?.abort();
    };
  }, []);
  const config = snapshot.state.data?.configuration;
  const expectedRevision = revision ?? config?.configRevision ?? 0;
  async function submit(
    action: string,
    payload: unknown,
    mutate: (
      identity: ReturnType<typeof configIdentity>,
      signal: AbortSignal,
    ) => Promise<ConfigReceipt>,
  ) {
    if (submitting.current || !config || snapshot.state.status !== 'READY') return;
    submitting.current = true;
    const signature = JSON.stringify({ action, expectedRevision, payload });
    const identity = configIdentity(
      expectedRevision,
      attempt.current?.signature === signature ? attempt.current.requestId : undefined,
    );
    attempt.current = { signature, requestId: identity.requestId };
    controller.current = new AbortController();
    setBusy(true);
    setResult(null);
    try {
      const outcome = await submitConfigurationMutation(
        port,
        (signal) => mutate(identity, signal),
        controller.current.signal,
      );
      if (!alive.current) return;
      setResult(outcome);
      if (outcome.kind === 'SAVED') {
        attempt.current = null;
        setRevision(outcome.receipt.configRevision);
        setEnableTarget(null);
        setReason('');
        snapshot.retry();
      }
    } catch {
      /* Unmount abort: the server receipt is verified by the next read. */
    } finally {
      submitting.current = false;
      if (alive.current) setBusy(false);
    }
  }
  const spec = METRIC_SPEC[sourceDraft.metric];
  return (
    <Card zone="nm-configuration" title="监测来源与区域">
      <MonitoringNetworkView state={snapshot.state} retry={snapshot.retry} />
      <button
        type="button"
        className="nm-config-toggle"
        aria-expanded={open}
        aria-controls="nm-configuration-panel"
        onClick={() => {
          if (!open && revision === null) setRevision(config?.configRevision ?? null);
          setOpen(!open);
        }}
      >
        监测来源/区域配置
      </button>
      {open && (
        <div
          id="nm-configuration-panel"
          data-nm-configuration
          data-nm-config-revision={config?.configRevision}
        >
          <p className="nm-scope">
            本地演练配置 · 操作预期修订{' '}
            <span data-nm-expected-revision>{expectedRevision || '等待读取'}</span> ·
            实际最新读取修订 {config?.configRevision ?? '未知'}
          </p>
          <p className="nm-notice">
            来源标识是合成监测来源。停用仅停止接纳新实时输入，不控制设备。停用不会解除预警，已形成预警的恢复要求与历史依据保留。重新启用需等待本周期新鲜观测。
          </p>
          {result?.kind === 'ERROR' && (
            <p className="nm-error" role="status" data-nm-config-error>
              配置失败：{result.message}；输入已保留，可核对后重试。
            </p>
          )}
          {result?.kind === 'CONFLICT' && (
            <div className="nm-error" role="status" data-nm-config-conflict>
              <p>409 CONFIG_REVISION_CONFLICT · 输入已保留，未重试写入。</p>
              <p>
                最新配置修订：{result.latest?.configRevision ?? '读取失败，版本未知'} ·{' '}
                {result.latest?.generatedAt ?? '请先重试读取最新配置'}
              </p>
              {result.latest && (
                <>
                  <p>
                    最新区域：
                    {result.latest.areas.map((a) => `${a.areaId} ${a.displayName}`).join('；') ||
                      '无'}{' '}
                    · 最新来源：
                    {result.latest.sources
                      .map(
                        (s) =>
                          `${s.sourceId} ${s.displayName} ${s.enabled ? '启用' : '停用'} rev${s.revision}`,
                      )
                      .join('；') || '无'}
                  </p>
                  <button
                    type="button"
                    data-nm-use-latest
                    onClick={() => {
                      setRevision(result.latest?.configRevision ?? null);
                      setResult(null);
                      snapshot.retry();
                    }}
                  >
                    使用最新修订保留草稿
                  </button>
                </>
              )}
              {!result.latest && (
                <button
                  type="button"
                  onClick={() => {
                    const c = new AbortController();
                    controller.current = c;
                    void port
                      .configuration(c.signal)
                      .then((latest) => {
                        if (alive.current) setResult({ kind: 'CONFLICT', latest });
                      })
                      .catch(() => {});
                  }}
                >
                  读取最新配置
                </button>
              )}
            </div>
          )}
          {result?.kind === 'SAVED' && (
            <p
              role="status"
              className="nm-receipt"
              data-nm-config-receipt
              data-nm-receipt-revision={result.receipt.configRevision}
            >
              配置已保存；观测到达需等待新数据。修订 {result.receipt.configRevision} · 对象修订{' '}
              {result.receipt.objectRevision} · epoch {result.receipt.epoch} ·{' '}
              {result.receipt.requestId} · {result.receipt.recordedAt}
            </p>
          )}
          <fieldset
            className="nm-config-fields"
            disabled={busy || snapshot.state.status !== 'READY' || result?.kind === 'CONFLICT'}
          >
            <legend>区域与来源配置 · 身份固定，显示名称可改</legend>
            <form
              className="nm-filters"
              onSubmit={(event) => {
                event.preventDefault();
                void submit('CREATE_AREA', areaDraft, (identity, signal) =>
                  port.createArea(
                    { ...identity, ...areaDraft, instanceId: CONFIG_INSTANCE },
                    signal,
                  ),
                );
              }}
            >
              <label>
                区域标识
                <input
                  aria-label="区域标识"
                  required
                  pattern="[a-z][a-z0-9-]{0,63}"
                  maxLength={64}
                  value={areaDraft.areaId}
                  onChange={(e) => setAreaDraft({ ...areaDraft, areaId: e.target.value })}
                />
              </label>
              <label>
                区域显示名称
                <input
                  aria-label="区域显示名称"
                  required
                  maxLength={128}
                  value={areaDraft.displayName}
                  onChange={(e) => setAreaDraft({ ...areaDraft, displayName: e.target.value })}
                />
              </label>
              <button type="submit">新增区域</button>
            </form>
            <p className="p01-muted">新区域可以暂时没有来源；该状态不代表正常覆盖。</p>
            <form
              className="nm-filters"
              onSubmit={(event) => {
                event.preventDefault();
                void submit('CREATE_SOURCE', sourceDraft, (identity, signal) =>
                  port.createSource(
                    { ...identity, ...sourceDraft, ...spec, instanceId: CONFIG_INSTANCE },
                    signal,
                  ),
                );
              }}
            >
              <label>
                来源所属区域
                <select
                  aria-label="来源所属区域"
                  required
                  value={sourceDraft.areaId}
                  onChange={(e) => setSourceDraft({ ...sourceDraft, areaId: e.target.value })}
                >
                  <option value="">选择区域</option>
                  {config?.areas.map((a) => (
                    <option key={a.areaId} value={a.areaId}>
                      {a.displayName} ({a.areaId})
                    </option>
                  ))}
                </select>
              </label>
              <label>
                来源标识
                <input
                  aria-label="来源标识"
                  required
                  pattern="[a-z][a-z0-9-]{0,63}"
                  maxLength={64}
                  value={sourceDraft.sourceId}
                  onChange={(e) => setSourceDraft({ ...sourceDraft, sourceId: e.target.value })}
                />
              </label>
              <label>
                来源显示名称
                <input
                  aria-label="来源显示名称"
                  required
                  maxLength={128}
                  value={sourceDraft.displayName}
                  onChange={(e) => setSourceDraft({ ...sourceDraft, displayName: e.target.value })}
                />
              </label>
              <label>
                指标
                <select
                  aria-label="指标"
                  value={sourceDraft.metric}
                  onChange={(e) =>
                    setSourceDraft({ ...sourceDraft, metric: e.target.value as Metric })
                  }
                >
                  {Object.keys(METRIC_SPEC).map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                单位
                <input aria-label="单位" readOnly value={spec.unit} />
              </label>
              <label>
                类别
                <input aria-label="类别" readOnly value={spec.category} />
              </label>
              <button type="submit">新增来源</button>
            </form>
            <p className="p01-muted">
              新增来源初始停用（epoch 0）。显式启用后仍需等待新数据；不同 sourceId
              不证明物理设备独立。
            </p>
            <form
              className="nm-filters"
              onSubmit={(event) => {
                event.preventDefault();
                void submit('RENAME_AREA', areaRename, (identity, signal) =>
                  port.renameArea(
                    areaRename.id,
                    { ...identity, displayName: areaRename.name },
                    signal,
                  ),
                );
              }}
            >
              <label>
                编辑区域
                <select
                  aria-label="编辑区域"
                  required
                  value={areaRename.id}
                  onChange={(e) =>
                    setAreaRename({
                      id: e.target.value,
                      name:
                        config?.areas.find((a) => a.areaId === e.target.value)?.displayName ?? '',
                    })
                  }
                >
                  <option value="">选择区域</option>
                  {config?.areas.map((a) => (
                    <option key={a.areaId} value={a.areaId}>
                      {a.displayName} ({a.areaId})
                    </option>
                  ))}
                </select>
              </label>
              <label>
                区域新显示名称
                <input
                  aria-label="区域新显示名称"
                  required
                  maxLength={128}
                  value={areaRename.name}
                  onChange={(e) => setAreaRename({ ...areaRename, name: e.target.value })}
                />
              </label>
              <button type="submit">保存区域显示名称</button>
            </form>
            <form
              className="nm-filters"
              onSubmit={(event) => {
                event.preventDefault();
                void submit('RENAME_SOURCE', sourceRename, (identity, signal) =>
                  port.renameSource(
                    sourceRename.id,
                    { ...identity, displayName: sourceRename.name },
                    signal,
                  ),
                );
              }}
            >
              <label>
                编辑来源
                <select
                  aria-label="编辑来源"
                  required
                  value={sourceRename.id}
                  onChange={(e) =>
                    setSourceRename({
                      id: e.target.value,
                      name:
                        config?.sources.find((s) => s.sourceId === e.target.value)?.displayName ??
                        '',
                    })
                  }
                >
                  <option value="">选择来源</option>
                  {config?.sources.map((s) => (
                    <option key={s.sourceId} value={s.sourceId}>
                      {s.displayName} ({s.sourceId})
                    </option>
                  ))}
                </select>
              </label>
              <label>
                来源新显示名称
                <input
                  aria-label="来源新显示名称"
                  required
                  maxLength={128}
                  value={sourceRename.name}
                  onChange={(e) => setSourceRename({ ...sourceRename, name: e.target.value })}
                />
              </label>
              <button type="submit">保存来源显示名称</button>
            </form>
            <div className="nm-table-wrap">
              <table className="nm-table">
                <caption>来源启停 · 必须填写原因</caption>
                <thead>
                  <tr>
                    <th>来源 / 区域</th>
                    <th>意图 / 配置修订 / 周期</th>
                    <th>配置动作</th>
                  </tr>
                </thead>
                <tbody>
                  {config?.sources.map((s) => (
                    <tr key={s.sourceId} data-nm-config-source={s.sourceId}>
                      <td>
                        {s.displayName}
                        <small>
                          {s.sourceId} / {s.areaId}
                        </small>
                      </td>
                      <td>
                        {s.enabled ? '启用' : '停用'} · revision {s.revision} · epoch {s.epoch}
                      </td>
                      <td>
                        <button
                          type="button"
                          aria-label={`${s.enabled ? '停用' : '启用'}来源 ${s.sourceId}`}
                          onClick={() => {
                            setEnableTarget(s);
                            setReason('');
                          }}
                        >
                          {s.enabled ? '停用' : '启用'}来源 {s.sourceId}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {enableTarget && (
              <form
                className="nm-filters nm-enable-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  void submit(
                    'SET_ENABLED',
                    { id: enableTarget.sourceId, enabled: !enableTarget.enabled, reason },
                    (identity, signal) =>
                      port.setEnabled(
                        enableTarget.sourceId,
                        { ...identity, enabled: !enableTarget.enabled, reason },
                        signal,
                      ),
                  );
                }}
              >
                <p>
                  {enableTarget.enabled ? '停用' : '启用'} {enableTarget.sourceId}
                  ：停用不会解除预警；重新启用需要新周期合格数据。
                </p>
                <label>
                  启停原因
                  <input
                    aria-label="启停原因"
                    required
                    maxLength={256}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </label>
                <button type="submit">确认启停</button>
                <button
                  type="button"
                  className="nm-secondary"
                  onClick={() => setEnableTarget(null)}
                >
                  取消启停
                </button>
              </form>
            )}
          </fieldset>
          {busy && (
            <p role="status" aria-busy="true">
              正在提交配置…
            </p>
          )}
          <ConfigurationHistory port={port} />
        </div>
      )}
    </Card>
  );
}
